import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import type { ClientsParamsSetupColumn } from "@nexus/contracts";
import { CUSTOMER_SETUP_DISPLAY_COLUMNS, toCustomerSetupDisplay } from "@/features/identity-hub/lib/customer-setup";
import { SettingsRow } from "../shared/SettingsRow";
import { customerSetupLabel } from "./CustomerSetupFields";

/** Nexus's saved customer setup snapshot. It is written once and never updated, so there are no controls. */
export function CustomerSetupReadOnly({ values }: { values: Record<string, unknown> }) {
  const { t } = useTranslation();

  return (
    <SettingsRow
      data-testid="customer-setup-read-only"
      title={
        <span className="flex items-center gap-1.5">
          <Lock className="size-3 text-muted-foreground" aria-hidden />
          {t("iam.onboarding.setup.title", "Data setup")}
        </span>
      }
      hint={t(
        "iam.onboarding.setup.readOnlyHint",
        "Set once when the customer was onboarded. Data processing starts from it, so it can't be changed in Nexus.",
      )}
    >
      <dl className="grid grid-cols-[minmax(140px,auto)_1fr] gap-x-4 gap-y-2">
        {CUSTOMER_SETUP_DISPLAY_COLUMNS.map((column) => (
          <SetupValue key={column} column={column} value={values[column]} />
        ))}
      </dl>
    </SettingsRow>
  );
}

function SetupValue({ column, value }: { column: ClientsParamsSetupColumn; value: unknown }) {
  const { t } = useTranslation();
  const display = toCustomerSetupDisplay(column, value);
  const text =
    display.kind === "empty"
      ? t("iam.shared.none", "None")
      : display.kind === "bool"
        ? display.value
          ? t("iam.onboarding.setup.yes", "Yes")
          : t("iam.onboarding.setup.no", "No")
        : display.kind === "monthYear"
          ? `${display.month}/${display.year}`
          : display.text;

  return (
    <>
      <dt className="text-xs font-medium text-muted-foreground">{customerSetupLabel(t, column)}</dt>
      <dd className="min-w-0 break-words text-xs text-foreground" data-testid={`customer-setup-value-${column}`}>
        {text}
      </dd>
    </>
  );
}
